import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { environment } from '@env';
import { CLIENT_LOOKUP_REPOSITORY } from '../domain/repositories/client-lookup.repository';
import { LOCAL_USERS_REPOSITORY } from '../domain/repositories/local-users.repository';
import { MOBILE_USERS_REPOSITORY } from '../domain/repositories/mobile-users.repository';
import { ROLES_REPOSITORY } from '../domain/repositories/roles.repository';
import { UNLINKED_VETERINARY_USERS_REPOSITORY } from '../domain/repositories/unlinked-veterinary-users.repository';
import { ORGANIZATION_PROFILE_REPOSITORY } from '../domain/repositories/organization-profile.repository';
import { NOTIFICATION_SETTINGS_REPOSITORY } from '../domain/repositories/notification-settings.repository';
import {
  MOBILE_CONSULTATION_SECTIONS_REPOSITORY,
} from '../domain/repositories/mobile-consultation-sections.repository';
import { DEPARTMENTS_ADMIN_REPOSITORY } from '../domain/repositories/departments-admin.repository';
import { ORGANIZATION_SUBSCRIPTION_REPOSITORY } from '../domain/repositories/organization-subscription.repository';
import { ClientLookupRemoteDatasource } from './datasources/remote/client-lookup.remote-datasource';
import { LocalUsersRemoteDatasource } from './datasources/remote/local-users.remote-datasource';
import { MobileUsersRemoteDatasource } from './datasources/remote/mobile-users.remote-datasource';
import { RolesRemoteDatasource } from './datasources/remote/roles.remote-datasource';
import { UnlinkedVeterinaryUsersRemoteDatasource } from './datasources/remote/unlinked-veterinary-users.remote-datasource';
import { OrganizationProfileRemoteDatasource } from './datasources/remote/organization-profile.remote-datasource';
import { NotificationSettingsRemoteDatasource } from './datasources/remote/notification-settings.remote-datasource';
import {
  MobileConsultationSectionsRemoteDatasource,
} from './datasources/remote/mobile-consultation-sections.remote-datasource';
import { DepartmentsAdminRemoteDatasource } from './datasources/remote/departments-admin.remote-datasource';
import { OrganizationSubscriptionRemoteDatasource } from './datasources/remote/organization-subscription.remote-datasource';
import {
  OrganizationSubscriptionPaymentMethodFakeDatasource,
} from './datasources/fake/organization-subscription-payment-method.fake-datasource';
import { ClientLookupMapper } from './mappers/client-lookup.mapper';
import { LocalUserMapper } from './mappers/local-user.mapper';
import { MobileUserMapper } from './mappers/mobile-user.mapper';
import { RoleMapper } from './mappers/role.mapper';
import { UnlinkedVeterinaryUserMapper } from './mappers/unlinked-veterinary-user.mapper';
import { OrganizationProfileMapper } from './mappers/organization-profile.mapper';
import { NotificationSettingsMapper } from './mappers/notification-settings.mapper';
import { MobileConsultationSectionsMapper } from './mappers/mobile-consultation-sections.mapper';
import { DepartmentAdminMapper } from './mappers/department-admin.mapper';
import { DepartmentProfileMapper } from './mappers/department-profile.mapper';
import { OrganizationSubscriptionMapper } from './mappers/organization-subscription.mapper';
import { ClientLookupRepositoryImpl } from './repositories/client-lookup.repository.impl';
import { LocalUsersRepositoryImpl } from './repositories/local-users.repository.impl';
import { MobileUsersRepositoryImpl } from './repositories/mobile-users.repository.impl';
import { RolesRepositoryImpl } from './repositories/roles.repository.impl';
import { UnlinkedVeterinaryUsersRepositoryImpl } from './repositories/unlinked-veterinary-users.repository.impl';
import { OrganizationProfileRepositoryImpl } from './repositories/organization-profile.repository.impl';
import { NotificationSettingsRepositoryImpl } from './repositories/notification-settings.repository.impl';
import {
  MobileConsultationSectionsRepositoryImpl,
} from './repositories/mobile-consultation-sections.repository.impl';
import { DepartmentsAdminRepositoryImpl } from './repositories/departments-admin.repository.impl';
import { OrganizationSubscriptionRepositoryImpl } from './repositories/organization-subscription.repository.impl';
import { GEOCODING_REPOSITORY } from '../domain/repositories/geocoding.repository';
import { NominatimRemoteDatasource } from './datasources/remote/nominatim.remote-datasource';
import { GeocodedPlaceMapper } from './mappers/geocoded-place.mapper';
import { GeocodingRepositoryImpl } from './repositories/geocoding.repository.impl';

export function provideSettingsData(): EnvironmentProviders {
  return makeEnvironmentProviders([
    LocalUsersRemoteDatasource,
    MobileUsersRemoteDatasource,
    ClientLookupRemoteDatasource,
    RolesRemoteDatasource,
    UnlinkedVeterinaryUsersRemoteDatasource,
    OrganizationProfileRemoteDatasource,
    NotificationSettingsRemoteDatasource,
    MobileConsultationSectionsRemoteDatasource,
    DepartmentsAdminRemoteDatasource,
    // Only the saved-card calls are ever faked, and only on a local host -- see the flag's
    // comment in environment.ts.
    {
      provide: OrganizationSubscriptionRemoteDatasource,
      useClass: environment.useClinicPaymentMethodFake
        ? OrganizationSubscriptionPaymentMethodFakeDatasource
        : OrganizationSubscriptionRemoteDatasource,
    },
    NominatimRemoteDatasource,
    LocalUserMapper,
    MobileUserMapper,
    ClientLookupMapper,
    RoleMapper,
    UnlinkedVeterinaryUserMapper,
    OrganizationProfileMapper,
    NotificationSettingsMapper,
    MobileConsultationSectionsMapper,
    DepartmentAdminMapper,
    DepartmentProfileMapper,
    OrganizationSubscriptionMapper,
    GeocodedPlaceMapper,
    { provide: LOCAL_USERS_REPOSITORY, useClass: LocalUsersRepositoryImpl },
    { provide: MOBILE_USERS_REPOSITORY, useClass: MobileUsersRepositoryImpl },
    { provide: CLIENT_LOOKUP_REPOSITORY, useClass: ClientLookupRepositoryImpl },
    { provide: ROLES_REPOSITORY, useClass: RolesRepositoryImpl },
    { provide: UNLINKED_VETERINARY_USERS_REPOSITORY, useClass: UnlinkedVeterinaryUsersRepositoryImpl },
    { provide: ORGANIZATION_PROFILE_REPOSITORY, useClass: OrganizationProfileRepositoryImpl },
    { provide: NOTIFICATION_SETTINGS_REPOSITORY, useClass: NotificationSettingsRepositoryImpl },
    { provide: MOBILE_CONSULTATION_SECTIONS_REPOSITORY, useClass: MobileConsultationSectionsRepositoryImpl },
    { provide: DEPARTMENTS_ADMIN_REPOSITORY, useClass: DepartmentsAdminRepositoryImpl },
    { provide: ORGANIZATION_SUBSCRIPTION_REPOSITORY, useClass: OrganizationSubscriptionRepositoryImpl },
    { provide: GEOCODING_REPOSITORY, useClass: GeocodingRepositoryImpl },
  ]);
}
